import {
  CORP_HOMEPAGE_TITLE,
  isCorpTenant,
  useUserContext,
} from "@capone/common";
import {
  AVAILABLE,
  CONTROL,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  useExperimentsContext,
} from "@capone/experiments";
import { Box, Container, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  CorporateTypography,
  Icon,
  IconName,
  LandingBenefits,
  PremiumStaysInfoAccordian,
  TravelAppFTUXModal,
} from "halifax";
import React, { useMemo } from "react";
import {
  CallState,
  Funnel,
  RewardsAccount,
  Tenant,
  WalletDetailsResponse,
} from "redmond";
import { FreeDisruptionRebookingSearchBanner } from "@capone-corporate/portal";
import { useLocation } from "react-router-dom";
import { PremierCollectionLandingPageBenefits } from "../../../../../cap1-modules/premier-collection-module/src/modules/search/components/PremierCollectionLandingPageBenefits";
import { fetchTravelWalletDetails } from "../../api/v1/takeovers/fetchTravelWalletDetails";
import { trackEvent } from "../../api/v1/trackEvent";

import {
  ANNUAL_TRAVEL_CREDITS,
  CASH_VALUEPROP_EXPERIMENT,
  CASH_VALUEPROP_VARIANTS,
  HOTELS_HOMEPAGE_CROSS_SELL_EXPERIMENT,
  LC_GATING_EXPERIMENT,
  NOT_SELF_FUNDED,
  PACKAGES_EXPERIMENT,
  PACKAGES_EXPERIMENT_VARIANTS,
  PREMIER_COLLECTION_EXPERIMENT,
  SELF_FUNDED,
  TRAVEL_APP_GTM_EXPERIMENT,
  TRAVEL_SALE,
  TRAVEL_SALE_ACTIVE,
  TRAVEL_SALE_VARIANTS,
  VR_FOR_NON_PREMIUM_CARDHOLDERS_EXPERIMENT,
  VR_FOR_PREMIUM_CARDHOLDERS_EXPERIMENT,
  XMOD_V1_EXPERIMENT,
} from "../../context/experiments";
import config from "../../utils/config";
import { RecentlyViewedAndSearchedProps } from "../Body/recently-viewed-and-searched-helpers";
import { HomepageAnnualTravelCredit } from "../HomepageAnnualTravelCredit";
import { HomepageTravelSale } from "../HomepageTravelSale/component";
import LandingPageNavigation from "../LandingPageNavigation";
import { RecentlyViewedAndSearched } from "../RecentlyViewedAndSearched";
import { CorpHomePageBottomSection } from "../capone-corporate/CorpHomePageBottomSection/component";
import { CorpHomePageTopSection } from "../capone-corporate/CorpHomePageTopSection/component";
import { getAdsCarouselByTenant } from "../AdsCarousel";
import "./styles.scss";
import * as textConstants from "./textConstants";

export enum LandingPageType {
  FLIGHTS = "flights",
  HOTELS = "hotels",
  STAYS = "stays",
  CARS = "cars",
  PREMIUM_STAYS = "premium-stays",
  PACKAGES = "packages",
  EXPERIENCES = "activities",
}

export interface MobileLandingPageProps {
  type: LandingPageType;
  rewardsAccounts: RewardsAccount[];
  selectedRewardsAccountId: string | null;
  recentlyViewedAndSearchedProps?: RecentlyViewedAndSearchedProps | null;
  recentlyViewedAndSearchedLoaded: boolean;
  fetchRewardsAccountsCallState: CallState;
}

export const MobileLandingPage = ({
  type,
  rewardsAccounts,
  selectedRewardsAccountId,
  recentlyViewedAndSearchedProps,
  recentlyViewedAndSearchedLoaded,
  fetchRewardsAccountsCallState,
}: MobileLandingPageProps) => {
  const { sessionInfo } = useUserContext(config.TENANT);
  const [largestValueAccount, setLargestValueAccount] = React.useState<
    RewardsAccount | undefined
  >(undefined);
  const [walletDetails, setWalletDetails] =
    React.useState<WalletDetailsResponse | null>(null);
  const [atcBannerVisible, setAtcBannerVisible] =
    React.useState<boolean>(false);
  const [openTravelCreditFTUX, setOpenTravelCreditFTUX] =
    React.useState<boolean>(false);

  const expState = useExperimentsContext();
  const location = useLocation();

  const cashValuePropVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    CASH_VALUEPROP_EXPERIMENT,
    CASH_VALUEPROP_VARIANTS
  );

  const crossSellExperiment = getExperimentVariant(
    expState.experiments,
    HOTELS_HOMEPAGE_CROSS_SELL_EXPERIMENT
  );

  const isCrossSellExperiment = React.useMemo(
    () => crossSellExperiment === AVAILABLE,
    [crossSellExperiment]
  );

  const isPremierCollectionEnabled =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT
    ) === AVAILABLE;

  const isLifestyleOnlyCollectionEnabled =
    getExperimentVariant(expState.experiments, LC_GATING_EXPERIMENT) ===
      AVAILABLE && !isPremierCollectionEnabled;

  const packagesExperimentVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    PACKAGES_EXPERIMENT,
    PACKAGES_EXPERIMENT_VARIANTS
  );

  const isPackagesExperiment = [SELF_FUNDED, NOT_SELF_FUNDED].includes(
    packagesExperimentVariant
  );

  const isXmodRedesign = getExperimentVariant(
    expState.experiments,
    XMOD_V1_EXPERIMENT
  ).startsWith(AVAILABLE);

  React.useEffect(() => {
    const earnMultiplier = (account: RewardsAccount) =>
      account.earn.hotelsMultiplier ?? -1;
    setLargestValueAccount(
      rewardsAccounts.sort(
        (prev, current) => earnMultiplier(current) - earnMultiplier(prev)
      )[0]
    );
  }, [rewardsAccounts]);

  React.useEffect(() => {
    fetchTravelWalletDetails()
      .then((response) => setWalletDetails(response))
      .catch(() => null);
  }, []);

  const hasOnlyCashCards = React.useMemo(
    () =>
      rewardsAccounts?.length > 0 &&
      rewardsAccounts.every(
        (account) => account.rewardsBalance.currency.toLowerCase() === "cash"
      ),
    [rewardsAccounts]
  );

  const benefitsVariant = React.useMemo(() => {
    switch (cashValuePropVariant) {
      case "cash-valueprop-a":
        return textConstants.CASH_CARD_BENEFITS.VARIANT_A;
      case "cash-valueprop-b":
        return textConstants.CASH_CARD_BENEFITS.VARIANT_B;
      case "cash-valueprop-c":
        return textConstants.CASH_CARD_BENEFITS.VARIANT_C;
      case "control":
      default:
        return [];
    }
  }, [cashValuePropVariant]);

  const isAnnualTravelCreditsExperiment =
    getExperimentVariant(expState.experiments, ANNUAL_TRAVEL_CREDITS) ===
    AVAILABLE;

  const travelSaleVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    TRAVEL_SALE,
    TRAVEL_SALE_VARIANTS
  );

  const isTravelSaleEnabled = travelSaleVariant !== CONTROL;

  const VRForPremiumCardHolders = getExperimentVariant(
    expState.experiments,
    VR_FOR_PREMIUM_CARDHOLDERS_EXPERIMENT
  );
  const isVRForPremiumCardHoldersEnabled = useMemo(
    () => VRForPremiumCardHolders === AVAILABLE,
    [VRForPremiumCardHolders]
  );

  const VRForNonPremiumCardHolders = getExperimentVariant(
    expState.experiments,
    VR_FOR_NON_PREMIUM_CARDHOLDERS_EXPERIMENT
  );
  const isVRForNonPremiumCardHoldersEnabled = useMemo(
    () => VRForNonPremiumCardHolders === AVAILABLE,
    [VRForNonPremiumCardHolders]
  );

  const isTravelAppGTMEnabled =
    getExperimentVariant(expState.experiments, TRAVEL_APP_GTM_EXPERIMENT) ===
    AVAILABLE;

  const getMobileLandingTitle = (tenant: Tenant) => {
    if (isCorpTenant(tenant)) {
      return <span>{CORP_HOMEPAGE_TITLE}</span>;
      // eslint-disable-next-line no-else-return
    } else if (
      isPackagesExperiment &&
      type === LandingPageType.PACKAGES &&
      largestValueAccount
    ) {
      return textConstants.PACKAGES_TITLE;
    } else if (
      !(
        type === LandingPageType.PREMIUM_STAYS &&
        (isLifestyleOnlyCollectionEnabled || isPremierCollectionEnabled)
      )
    ) {
      return textConstants.getRedesignVariant3Title(
        type,
        fetchRewardsAccountsCallState,
        largestValueAccount
      );
    } else if (type === LandingPageType.PREMIUM_STAYS) {
      if (
        isLifestyleOnlyCollectionEnabled &&
        !isVRForNonPremiumCardHoldersEnabled
      ) {
        return textConstants.LC_NON_PREMIUM_TITLE;
        // eslint-disable-next-line no-else-return
      } else if (
        isPremierCollectionEnabled ||
        isVRForNonPremiumCardHoldersEnabled
      ) {
        return textConstants.LC_PREMIUM_TITLE;
      }
    }
    return (
      <>
        <span>{textConstants.TITLE_FIRST_HALF_BOLD}</span>
        {textConstants.TITLE_SECOND_HALF}
      </>
    );
  };

  const showRecentlyViewed =
    (isCorpTenant(config.TENANT) || recentlyViewedAndSearchedProps) &&
    type !== LandingPageType.PREMIUM_STAYS;

  const showCashCardBenefits =
    cashValuePropVariant !== "control" && hasOnlyCashCards;

  const showPremiumBenefits =
    type === LandingPageType.PREMIUM_STAYS && largestValueAccount;

  const showAdsCarousel =
    type === LandingPageType.HOTELS ||
    type === LandingPageType.STAYS ||
    type === LandingPageType.FLIGHTS;

  const hasContentBelowFold =
    atcBannerVisible ||
    showRecentlyViewed ||
    showCashCardBenefits ||
    showPremiumBenefits ||
    showAdsCarousel;

  const searchSubtitle = () => {
    if (
      type === LandingPageType.PREMIUM_STAYS &&
      isVRForNonPremiumCardHoldersEnabled
    ) {
      return textConstants.VR_NON_PREMIUM_SUBTITLE;
    }
    if (
      type === LandingPageType.PREMIUM_STAYS &&
      isLifestyleOnlyCollectionEnabled &&
      !isVRForNonPremiumCardHoldersEnabled
    ) {
      return textConstants.LC_NON_PREMIUM_SUBTITLE;
    }
    if (type === LandingPageType.PREMIUM_STAYS && isPremierCollectionEnabled) {
      return textConstants.LC_PREMIUM_SUBTITLE;
    }

    if (
      !(
        (type === LandingPageType.PREMIUM_STAYS &&
          (isLifestyleOnlyCollectionEnabled || isPremierCollectionEnabled)) ||
        (type === LandingPageType.PACKAGES && isPackagesExperiment)
      )
    ) {
      return textConstants.getRedesignVariant3Subtitle(
        type,
        fetchRewardsAccountsCallState,
        config.TENANT
      );
    }

    if (
      type === LandingPageType.PACKAGES &&
      isPackagesExperiment &&
      largestValueAccount
    ) {
      return textConstants.PACKAGES_SUBTITLE(largestValueAccount);
    }

    return "";
  };

  const funnel = useMemo(() => {
    switch (type) {
      case LandingPageType.FLIGHTS:
        return Funnel.Air;
      case LandingPageType.HOTELS:
        return Funnel.Lodging;
      case LandingPageType.STAYS:
        return Funnel.Homes;
      case LandingPageType.CARS:
        return Funnel.Ground;
      case LandingPageType.PREMIUM_STAYS:
        return Funnel.Homes;
      case LandingPageType.PACKAGES:
        return Funnel.Lodging;
      case LandingPageType.EXPERIENCES:
        return Funnel.Experiences;
      default:
        return undefined;
    }
  }, [type]);

  return (
    <>
      <Container
        className={clsx("landing-page", type, config.TENANT, {
          "homepage-cross-sell": isCrossSellExperiment,
          "only-lifestyle-collection":
            type === LandingPageType.PREMIUM_STAYS &&
            isLifestyleOnlyCollectionEnabled &&
            !isVRForNonPremiumCardHoldersEnabled,
          "with-lifestyle-collection":
            type === LandingPageType.PREMIUM_STAYS &&
            isPremierCollectionEnabled,
          "with-vacation-rentals":
            type === LandingPageType.PREMIUM_STAYS &&
            (isVRForPremiumCardHoldersEnabled ||
              isVRForNonPremiumCardHoldersEnabled),
          "show-travel-sale":
            type !== LandingPageType.PREMIUM_STAYS && isTravelSaleEnabled,
          "has-content-below-fold": hasContentBelowFold,
          xmod: isXmodRedesign,
        })}
      >
        <Box className="content">
          {isCorpTenant(config.TENANT) ? (
            <CorporateTypography variant="h4" className="search-title">
              {CORP_HOMEPAGE_TITLE}
            </CorporateTypography>
          ) : (
            <Typography variant="h2" className="search-title">
              {getMobileLandingTitle(config.TENANT)}
            </Typography>
          )}
          {isCorpTenant(config.TENANT) ? (
            <CorporateTypography variant="body1" className="search-subtitle">
              {searchSubtitle()}
            </CorporateTypography>
          ) : (
            <Typography variant="body1" className="search-subtitle">
              {searchSubtitle()}
            </Typography>
          )}

          <LandingPageNavigation type={type} />
          {isCorpTenant(config.TENANT) &&
            type === LandingPageType.FLIGHTS && (
              <FreeDisruptionRebookingSearchBanner className="fdr-search-banner-mobile" />
            )}
        </Box>
        {largestValueAccount &&
          type === LandingPageType.PREMIUM_STAYS &&
          (isVRForPremiumCardHoldersEnabled ||
            isVRForNonPremiumCardHoldersEnabled) && (
            <Box className="view-travel-benefits-wrapper">
              <Box className="view-travel-benefits-text-and-icon-wrapper">
                <Icon name={IconName.FilledGiftIcon} />
                <Typography className="view-travel-benefits-text">
                  View my travel benefits
                </Typography>
              </Box>
              <Icon name={IconName.Dropdown} />
            </Box>
          )}
      </Container>

      {walletDetails && (
        <HomepageAnnualTravelCredit
          firstName={sessionInfo.userInfo.firstName}
          rewardsAccounts={rewardsAccounts}
          walletDetails={walletDetails}
          isAnnualTravelCreditsExperiment={isAnnualTravelCreditsExperiment}
          isMobile
          showBanner={type !== LandingPageType.PREMIUM_STAYS}
          onChangeVisibleBanners={(banners) =>
            setAtcBannerVisible(banners.length > 0)
          }
          onFtuxModalOpenChange={setOpenTravelCreditFTUX}
        />
      )}
      {isTravelAppGTMEnabled && (
        <TravelAppFTUXModal
          isAnyOtherFtuxOpen={openTravelCreditFTUX}
          currentRoute={location.pathname}
          trackEvent={trackEvent}
        />
      )}
      {type !== LandingPageType.PREMIUM_STAYS && isTravelSaleEnabled && (
        <HomepageTravelSale
          active={travelSaleVariant === TRAVEL_SALE_ACTIVE}
          offers={walletDetails?.offers || []}
          negativeMargin={!atcBannerVisible}
        />
      )}
      <>
        <CorpHomePageTopSection
          sessionInfo={sessionInfo}
          trackEvent={trackEvent}
          funnel={funnel}
          accounts={rewardsAccounts}
          selectedAccountReferenceId={selectedRewardsAccountId}
        />
        {showAdsCarousel && getAdsCarouselByTenant(config.TENANT)}
        {showRecentlyViewed && (
          <RecentlyViewedAndSearched
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...recentlyViewedAndSearchedProps}
            negativeMargin={false}
            loading={!recentlyViewedAndSearchedLoaded}
            showSkeleton={isCorpTenant(config.TENANT)}
          />
        )}
        <CorpHomePageBottomSection sessionInfo={sessionInfo} />
      </>
      {showCashCardBenefits && (
        <LandingBenefits
          title={textConstants.CASH_CARD_BENEFITS_TITLE(
            rewardsAccounts?.[0]?.productDisplayName
          )}
          benefits={benefitsVariant}
        />
      )}
      {showPremiumBenefits && (
        <>
          <PremierCollectionLandingPageBenefits
            isVentureX={
              largestValueAccount?.productDisplayName
                ?.toLowerCase()
                .includes("venture x") ?? false
            }
            stayType={undefined}
            travelWalletOffers={walletDetails?.offers ?? []}
          />

          <Box>
            <PremiumStaysInfoAccordian
              variant="with-lifestyle-collection"
              includesVacationRentals={
                isVRForPremiumCardHoldersEnabled ||
                isVRForNonPremiumCardHoldersEnabled
              }
              isMobile
              defaultClosed={
                isVRForPremiumCardHoldersEnabled ||
                isVRForNonPremiumCardHoldersEnabled
              }
              stayType={undefined}
              isXmodRedesign={isXmodRedesign}
            />
          </Box>
        </>
      )}
      {hasContentBelowFold && <div className="mobile-footer-spacer" />}
    </>
  );
};
